<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  Key,
  Plus,
  Trash2,
  Copy,
  ShieldAlert,
  Eye,
  EyeOff,
  Check,
  RefreshCw,
} from 'lucide-vue-next'
import Card from '@/components/ui/Card.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import Modal from '@/components/ui/Modal.vue'
import Badge from '@/components/ui/Badge.vue'
import Empty from '@/components/ui/Empty.vue'
import Spinner from '@/components/ui/Spinner.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import { createKey, listKeys, revokeKey, type APIKey, type CreateKeyResponse } from '@/api/keys'
import { useToast } from '@/composables/useToast'
import { copyToClipboard } from '@/composables/useClipboard'

const { t } = useI18n()
const toast = useToast()
const qc = useQueryClient()

const keysQuery = useQuery({
  queryKey: ['keys'],
  queryFn: () => listKeys(),
})

const showCreate = ref(false)
const showSecret = ref<CreateKeyResponse | null>(null)
const showRevoke = ref<APIKey | null>(null)
const secretVisible = ref(false)

const createForm = reactive({
  name: '',
})

const createMutation = useMutation({
  mutationFn: (name: string) => createKey({ name: name.trim() || 'untitled' }),
  onSuccess: (resp) => {
    showSecret.value = resp
    secretVisible.value = true
    showCreate.value = false
    createForm.name = ''
    void qc.invalidateQueries({ queryKey: ['keys'] })
  },
})

const revokeMutation = useMutation({
  mutationFn: (id: string) => revokeKey(id),
  onSuccess: () => {
    toast.success(t('toast.deletedSuccess'))
    showRevoke.value = null
    void qc.invalidateQueries({ queryKey: ['keys'] })
  },
})

function openCreate(): void {
  createForm.name = ''
  showCreate.value = true
}

function submitCreate(): void {
  createMutation.mutate(createForm.name)
}

function doRevoke(): void {
  if (!showRevoke.value) return
  revokeMutation.mutate(showRevoke.value.id)
}

function dismissSecret(): void {
  showSecret.value = null
  secretVisible.value = false
}

async function copySecret(): Promise<void> {
  const v = showSecret.value?.secret
  if (!v) return
  const ok = await copyToClipboard(v)
  if (ok) toast.success(t('settings.keys.copied'))
  else toast.warning(t('settings.keys.copyFailed'))
}

async function copyId(id: string): Promise<void> {
  await copyToClipboard(id)
}

function formatDate(ts?: number): string {
  if (!ts || ts <= 0) return '—'
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return '—'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function maskedSecret(s: string): string {
  if (s.length <= 12) return '•'.repeat(s.length)
  return `${s.slice(0, 6)}${'•'.repeat(Math.min(20, s.length - 10))}${s.slice(-4)}`
}

const sortedKeys = computed(() => {
  const list = keysQuery.data.value ?? []
  return [...list].sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0))
})
</script>

<template>
  <div class="space-y-6">
    <Card
      :title="t('settings.keys.title')"
      :subtitle="t('settings.keys.subtitle')"
    >
      <template #header>
        <Button variant="primary" size="sm" @click="openCreate">
          <template #icon><Plus :size="14" /></template>
          {{ t('settings.keys.create') }}
        </Button>
      </template>

      <div v-if="keysQuery.isLoading.value" class="flex items-center justify-center py-12">
        <Spinner :size="20" :label="t('common.loading')" />
      </div>

      <Empty
        v-else-if="sortedKeys.length === 0"
        :title="t('settings.keys.empty')"
        :description="t('settings.keys.emptyHint')"
        :icon="Key"
      >
        <Button variant="primary" size="sm" @click="openCreate">
          <template #icon><Plus :size="14" /></template>
          {{ t('settings.keys.create') }}
        </Button>
      </Empty>

      <ul v-else class="divide-y divide-border rounded-md border border-border">
        <li
          v-for="key in sortedKeys"
          :key="key.id"
          class="flex items-center gap-3 px-4 py-3 hover:bg-bg-hover"
        >
          <div class="flex h-9 w-9 items-center justify-center rounded-md bg-brand-500/15 text-brand-600 dark:text-brand-300 shrink-0">
            <Key :size="16" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="truncate text-sm font-semibold text-fg">{{ key.name || 'untitled' }}</span>
              <Badge v-if="key.revoked" variant="danger">{{ t('settings.keys.revoked') }}</Badge>
            </div>
            <div class="mt-0.5 flex items-center gap-3 text-xs text-fg-muted">
              <button
                type="button"
                class="inline-flex items-center gap-1 font-mono hover:text-fg"
                :title="t('settings.keys.copyId')"
                @click="copyId(key.id)"
              >
                <code class="truncate max-w-[260px]">{{ key.id }}</code>
                <Copy :size="11" />
              </button>
              <span>·</span>
              <span>{{ t('settings.keys.created') }}: {{ formatDate(key.created_at) }}</span>
              <template v-if="key.last_used">
                <span>·</span>
                <span>{{ t('settings.keys.lastUsed') }}: {{ formatDate(key.last_used) }}</span>
              </template>
            </div>
          </div>

          <Tooltip :content="t('settings.keys.revoke')" placement="top">
            <Button
              variant="ghost"
              size="sm"
              icon-only
              :disabled="key.revoked"
              :aria-label="t('settings.keys.revoke')"
              @click="showRevoke = key"
            >
              <Trash2 :size="14" class="text-danger" />
            </Button>
          </Tooltip>
        </li>
      </ul>

      <div class="mt-4 flex items-center justify-end">
        <Button variant="ghost" size="sm" :loading="keysQuery.isFetching.value" @click="() => qc.invalidateQueries({ queryKey: ['keys'] })">
          <template #icon><RefreshCw :size="12" /></template>
          {{ t('common.refresh') }}
        </Button>
      </div>
    </Card>

    <!-- Create modal -->
    <Modal v-model="showCreate" :title="t('settings.keys.create')" size="sm">
      <form class="space-y-3" @submit.prevent="submitCreate">
        <p class="text-xs text-fg-muted">{{ t('settings.keys.createHint') }}</p>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('common.name') }}</label>
          <Input
            v-model="createForm.name"
            type="text"
            :placeholder="t('settings.keys.namePlaceholder')"
            autofocus
          />
        </div>
      </form>
      <template #footer>
        <Button variant="ghost" size="sm" @click="showCreate = false">{{ t('common.cancel') }}</Button>
        <Button variant="primary" size="sm" :loading="createMutation.isPending.value" @click="submitCreate">
          {{ t('common.create') }}
        </Button>
      </template>
    </Modal>

    <!-- Secret reveal modal (only shown once) -->
    <Modal
      :model-value="showSecret !== null"
      :title="t('settings.keys.secretTitle')"
      size="md"
      :close-on-backdrop="false"
      :close-on-escape="false"
      @update:model-value="(v) => { if (!v) dismissSecret() }"
    >
      <div class="space-y-3">
        <div class="flex items-start gap-3 rounded-md border border-warning/40 bg-warning/10 p-3">
          <ShieldAlert :size="16" class="text-warning shrink-0 mt-0.5" />
          <p class="text-xs text-fg">
            {{ t('settings.keys.secretWarning') }}
          </p>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('common.name') }}</label>
          <div class="text-sm font-medium text-fg">{{ showSecret?.key.name }}</div>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('settings.keys.id') }}</label>
          <code class="block break-all rounded bg-bg-elevated px-2 py-1 font-mono text-xs text-fg">
            {{ showSecret?.key.id }}
          </code>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('settings.keys.secret') }}</label>
          <div class="flex items-stretch gap-2">
            <code class="flex-1 break-all rounded bg-bg-elevated px-2 py-1.5 font-mono text-xs text-fg">
              {{ secretVisible ? showSecret?.secret : maskedSecret(showSecret?.secret ?? '') }}
            </code>
            <Button
              variant="outline"
              size="sm"
              icon-only
              :aria-label="secretVisible ? t('settings.keys.hide') : t('settings.keys.show')"
              @click="secretVisible = !secretVisible"
            >
              <EyeOff v-if="secretVisible" :size="14" />
              <Eye v-else :size="14" />
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon-only
              :aria-label="t('settings.keys.copy')"
              @click="copySecret"
            >
              <Copy :size="14" />
            </Button>
          </div>
        </div>
      </div>
      <template #footer>
        <Button variant="primary" size="sm" @click="dismissSecret">
          <template #icon><Check :size="14" /></template>
          {{ t('settings.keys.iHaveSaved') }}
        </Button>
      </template>
    </Modal>

    <!-- Revoke confirm -->
    <Modal v-model="showRevoke" :title="t('settings.keys.revoke')" size="sm">
      <div v-if="showRevoke" class="flex items-start gap-3">
        <div class="rounded-md bg-danger/15 p-2 text-danger shrink-0">
          <ShieldAlert :size="18" />
        </div>
        <div class="text-sm text-fg-muted">
          <p>{{ t('settings.keys.revokeConfirm', { name: showRevoke.name }) }}</p>
          <p class="mt-2 text-xs text-fg-subtle">{{ t('settings.keys.revokeHint') }}</p>
        </div>
      </div>
      <template #footer>
        <Button variant="ghost" size="sm" @click="showRevoke = null">{{ t('common.cancel') }}</Button>
        <Button variant="danger" size="sm" :loading="revokeMutation.isPending.value" @click="doRevoke">
          {{ t('settings.keys.revoke') }}
        </Button>
      </template>
    </Modal>
  </div>
</template>
